import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { HttpResponse } from '@angular/common/http';
import { AuthService } from '../../services/auth.service';
import { AuthStateService } from '../../services/auth-state.service';

@Component({
  selector: 'hypenation-auth-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzCardModule,
    NzSpinModule,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent {
  emailForm: FormGroup;
  otpForm: FormGroup;
  step = signal<'email' | 'otp'>('email');
  loading = signal(false);
  email = signal<string>('');
  bannerImage = signal<string>('');

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _authService: AuthService,
    private readonly _notification: NzNotificationService,
    private readonly _router: Router,
    private readonly _route: ActivatedRoute,
    private readonly _authStateService: AuthStateService
  ) {
    // Set banner image based on app type
    const appType = this._authStateService.getAppType();
    if (appType === 'hac') {
      this.bannerImage.set('images/hac-banner.jpg');
    } else {
      // Default to ticketing app banner
      this.bannerImage.set('assets/home-page.jpg');
    }
    this.emailForm = this._fb.group({
      email: ['', [Validators.required, Validators.email]],
    });

    this.otpForm = this._fb.group({
      otp: ['', [Validators.required, Validators.minLength(4)]],
    });
  }

  onEmailSubmit(): void {
    if (this.emailForm.valid) {
      this.loading.set(true);
      const email = this.emailForm.get('email')?.value;
      this.email.set(email);

      this._authService
        .initiateAuthentication({ email })
        .subscribe({
          next: (res: HttpResponse<any>) => {
            if (res.body?.success) {
              this._notification.success(
                'OTP Sent',
                res.body.message || 'We\'ve sent an OTP to your email. Please check your inbox.'
              );
              this.step.set('otp');
              this.loading.set(false);
            } else {
              const msg = res.body?.message || 'We could not find an account with that email. Please check and try again.';
              this._notification.error('Unable to Sign In', msg);
              this.loading.set(false);
            }
          },
          error: (err) => {
            console.error('Initiate auth error:', err);
            const msg = err?.error?.message || 'Something went wrong. Please try again later.';
            this._notification.error('Unable to Sign In', msg);
            this.loading.set(false);
          },
        });
    } else {
      Object.values(this.emailForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }

  onOtpSubmit(): void {
    if (this.otpForm.valid) {
      this.loading.set(true);
      const otp = this.otpForm.get('otp')?.value;
      const email = this.email();

      this._authService
        .authenticate({ email, otp })
        .subscribe({
          next: (res: HttpResponse<any>) => {
            if (res.body?.accessToken) {
              // Store tokens and email
              const refreshToken = res.body.refreshToken;
              if (refreshToken) {
                this._authStateService.setTokens(res.body.accessToken, refreshToken);
              } else {
                this._authStateService.setToken(res.body.accessToken);
              }
              this._authStateService.setUserEmail(email);

              this._notification.success(
                'Login Successful',
                'You have been successfully authenticated!'
              );
              
              // Redirect to returnUrl if provided, otherwise to app-specific default
              const appType = this._authStateService.getAppType();
              const defaultRoute = appType === 'client-portal' ? '/' : '/tickets';
              const returnUrl = this._route.snapshot.queryParams['returnUrl'] || defaultRoute;
              this._router.navigate([returnUrl]);
            } else {
              this._notification.error(
                'Invalid OTP',
                'The OTP code you entered is invalid. Please try again.'
              );
              this.loading.set(false);
            }
          },
          error: (err) => {
            console.error('Authenticate error:', err);
            this._notification.error(
              'Authentication Failed',
              'Invalid OTP. Please try again.'
            );
            this.loading.set(false);
          },
        });
    } else {
      Object.values(this.otpForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }

  goBack(): void {
    this.step.set('email');
    this.otpForm.reset();
  }

  isClientPortal(): boolean {
    return this._authStateService.getAppType() === 'client-portal';
  }
}

